import type { Theme } from '@mui/material/styles';
import { TURN_STYLES } from '@/styles/theme';
import { VIOLET, VIOLET_LIGHT, VIOLET_TEXT } from './constants';

type ChipTone = { background: string; color: string; borderColor: string };

/** Shared light/dark tokens for Drug Test Reports tab surfaces. */
export const getDrugTestThemeTokens = (theme: Theme) => {
  const dark = theme.palette.mode === 'dark';

  return {
    dark,
    text: dark ? TURN_STYLES.darkSecondaryColor : TURN_STYLES.neutralDark,
    textMuted: dark ? '#9ca3af' : TURN_STYLES.primaryColor,
    textFaint: dark ? '#6b7280' : '#9ca3af',
    border: dark ? TURN_STYLES.darkBorderColor : TURN_STYLES.borderColor,
    borderSoft: dark ? TURN_STYLES.darkBorderColor : 'rgb(240, 242, 245)',
    surface: dark ? TURN_STYLES.darkSurfaceColor : TURN_STYLES.surfaceColor,
    surfaceMuted: dark ? '#1c1d2e' : '#fafafa',
    tableHeader: dark ? '#2a2c45' : '#efeff4',
    violet: dark ? TURN_STYLES.darkVioletDark : VIOLET,
    violetLight: dark ? 'rgba(91, 79, 214, 0.28)' : VIOLET_LIGHT,
    violetText: dark ? TURN_STYLES.darkVioletLight : VIOLET_TEXT,
    actionLink: dark ? TURN_STYLES.darkSecondaryAccentColor : VIOLET_TEXT,
    actionLinkHover: dark ? TURN_STYLES.darkSecondaryAccentColor : VIOLET,
    violetBorder: dark ? '#7c6ef0' : '#c4b5fd',
    errorBg: dark ? 'rgba(254, 202, 202, 0.12)' : TURN_STYLES.errorLight,
    errorBorder: dark ? TURN_STYLES.darkErrorDark : '#fecaca',
    errorText: dark ? TURN_STYLES.darkErrorLight : TURN_STYLES.errorDark,
    warningBg: dark ? 'rgba(253, 230, 138, 0.12)' : TURN_STYLES.warningLight,
    warningBorder: dark ? TURN_STYLES.darkWarningDark : '#fde047',
    warningText: dark ? TURN_STYLES.darkWarningLight : '#854d0e',
    noteBg: dark ? '#1c1d2e' : '#f9fafb',
    expired: dark ? TURN_STYLES.darkErrorLight : TURN_STYLES.errorDark,
    expiring: dark ? TURN_STYLES.darkWarningLight : '#854d0e',
    resolutionText: {
      clear: dark ? TURN_STYLES.darkSuccessLight : '#166534',
      consider: dark ? TURN_STYLES.darkErrorLight : TURN_STYLES.errorDark,
      processing: dark ? TURN_STYLES.darkInfoLight : '#1e40af',
      withdrawn: dark ? TURN_STYLES.darkWarningLight : '#854d0e',
    } as const,
    sourceBadge: {
      bgc_screening: {
        background: dark ? 'rgba(229, 231, 235, 0.12)' : TURN_STYLES.neutralLight,
        color: dark ? TURN_STYLES.darkNeutralLight : '#374151',
        borderColor: dark ? TURN_STYLES.darkBorderColor : TURN_STYLES.borderColor,
      },
      candidate_details: {
        background: dark ? 'rgba(191, 219, 254, 0.15)' : TURN_STYLES.infoLight,
        color: dark ? TURN_STYLES.darkInfoLight : '#1e40af',
        borderColor: dark ? TURN_STYLES.darkInfoDark : '#93c5fd',
      },
      api_single: {
        background: dark ? 'rgba(91, 79, 214, 0.28)' : VIOLET_LIGHT,
        color: dark ? TURN_STYLES.darkVioletLight : VIOLET_TEXT,
        borderColor: dark ? '#7c6ef0' : '#c4b5fd',
      },
    } satisfies Record<string, ChipTone>,
    resolutionBadge: {
      clear: {
        background: dark ? 'rgba(187, 247, 208, 0.15)' : TURN_STYLES.successLight,
        color: dark ? TURN_STYLES.darkSuccessLight : '#166534',
        borderColor: dark ? TURN_STYLES.darkSuccessDark : '#86efac',
      },
      consider: {
        background: dark ? 'rgba(254, 215, 170, 0.15)' : TURN_STYLES.orangeLight,
        color: dark ? TURN_STYLES.darkOrangeLight : '#c2410c',
        borderColor: dark ? TURN_STYLES.darkOrangeDark : '#fdba74',
      },
      processing: {
        background: dark ? 'rgba(191, 219, 254, 0.15)' : TURN_STYLES.infoLight,
        color: dark ? TURN_STYLES.darkInfoLight : '#1e40af',
        borderColor: dark ? TURN_STYLES.darkInfoDark : '#93c5fd',
      },
      withdrawn: {
        background: dark ? 'rgba(253, 230, 138, 0.15)' : TURN_STYLES.warningLight,
        color: dark ? TURN_STYLES.darkWarningLight : '#a16207',
        borderColor: dark ? TURN_STYLES.darkWarningDark : '#fde047',
      },
    } satisfies Record<string, ChipTone>,
    processingChip: {
      received: {
        background: dark ? 'rgba(229, 231, 235, 0.12)' : TURN_STYLES.neutralLight,
        color: dark ? TURN_STYLES.darkNeutralLight : '#374151',
      },
      processing: {
        background: dark ? 'rgba(191, 219, 254, 0.15)' : TURN_STYLES.infoLight,
        color: dark ? TURN_STYLES.darkInfoLight : '#1e40af',
      },
      fulfilled: {
        background: dark ? 'rgba(187, 247, 208, 0.15)' : TURN_STYLES.successLight,
        color: dark ? TURN_STYLES.darkSuccessLight : '#166534',
      },
      error: {
        background: dark ? 'rgba(254, 202, 202, 0.12)' : TURN_STYLES.errorLight,
        color: dark ? TURN_STYLES.darkErrorLight : TURN_STYLES.errorDark,
      },
      ignored: {
        background: dark ? 'rgba(253, 230, 138, 0.15)' : TURN_STYLES.warningLight,
        color: dark ? TURN_STYLES.darkWarningLight : '#854d0e',
      },
    },
  };
};
